import { useLayoutEffect, useMemo, useRef } from 'react';

import {
  DashboardDataTableStickyEdge,
  type DashboardDataTableColumn,
  type DashboardDataTableSortProps,
} from './DashboardDataTable.types';
import {
  dashboardDataTableDefaultStickyColumn,
  isDashboardDataTableNameColumn,
} from './DashboardDataTableSticky';

export function useDashboardDataTableStickyColumns<TItem>(
  inputColumns: DashboardDataTableColumn<TItem>[],
  sort?: DashboardDataTableSortProps,
  minWidthClassName?: string,
  fillHeight?: boolean,
) {
  const tableRef = useRef<HTMLTableElement>(null);
  const columns = useMemo(
    () => inputColumns.map(dashboardDataTableDefaultStickyColumn),
    [inputColumns],
  );
  const sortable = Boolean(sort);
  const nameIndex = inputColumns.findIndex(({ id }) => isDashboardDataTableNameColumn(id));

  useLayoutEffect(() => {
    const table = tableRef.current;
    const viewport = table?.parentElement;
    const headers = table?.tHead?.rows[0]?.cells;
    if (!table || !viewport || !headers) return;
    const resetWidths = () => {
      table.style.removeProperty('width');
      table.style.removeProperty('min-width');
      Array.from(headers).forEach((header) => {
        header.style.removeProperty('width');
        header.style.removeProperty('min-width');
      });
    };
    const update = () => {
      resetWidths();
      const widths = Array.from(headers, (header) => header.offsetWidth);
      const nameWidth = widths[nameIndex] ?? 0;
      const limit = viewport.clientWidth * 0.55;
      if (window.innerWidth < 640 && limit > 0 && nameWidth > limit) {
        widths[nameIndex] = limit;
        headers[nameIndex]?.style.setProperty('min-width', '0');
        // Freeze other columns at their natural widths so shrinking name cannot redistribute them.
        widths.forEach((width, index) => {
          headers[index]?.style.setProperty('width', `${String(width)}px`);
        });
        const total = widths.reduce((sum, width) => sum + width, 0);
        table.style.width = `${String(total)}px`;
        table.style.minWidth = `${String(total)}px`;
      }
      let left = 0;
      columns.forEach((column, index) => {
        if (column.sticky !== DashboardDataTableStickyEdge.Left) return;
        if (isDashboardDataTableNameColumn(column.id))
          table.style.setProperty('--table-name-left', `${String(left)}px`);
        left += widths[index] ?? 0;
      });
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(viewport);
    window.addEventListener('resize', update);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
      resetWidths();
    };
  }, [
    columns,
    nameIndex,
    sortable,
    sort?.value?.key,
    sort?.value?.direction,
    minWidthClassName,
    fillHeight,
  ]);

  return { columns, tableRef };
}
